import React, { useState, useEffect, useMemo } from 'react';
import { supabase, db } from '../lib/supabase';

const fmtIST = (d) => {
  const ist = new Date(d.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  return `${ist.getFullYear()}-${String(ist.getMonth()+1).padStart(2,'0')}-${String(ist.getDate()).padStart(2,'0')}`;
};

// ── Public Booking Page — aspreo.com/book/[username] ──────────────────────────
// No auth required. Anyone can visit this URL and book a meeting.
// On booking: saves to Supabase, sends emails via Resend edge fn, 
//             creates Google Calendar event (if host has connected Google)

const FONT = "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif";
const PRIMARY = '#2B1DC9';
const TEAL    = '#0D9488';
const DAYS_SHORT = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

export default function PublicBookingPage({ username }) {
  const [host,          setHost]          = useState(null);
  const [meetingTypes,  setMeetingTypes]  = useState([]);
  const [availability,  setAvailability]  = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState('');

  // Booking flow state
  const [step,      setStep]      = useState(0); // 0=type,1=date,2=slot,3=form,4=confirmed
  const [selType,   setSelType]   = useState(null);
  const [selDate,   setSelDate]   = useState('');
  const [selSlot,   setSelSlot]   = useState('');
  const [booking,   setBooking]   = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [form,      setForm]      = useState({ guest_name:'', guest_email:'', notes:'' });
  const [answers,   setAnswers]   = useState({});
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));

  // Load host profile + meeting config
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // Get host profile by booking_username — via the public view, since
        // profiles' own RLS blocks anon reads entirely (see migration 023).
        const { data: hostData, error: hErr } = await supabase
          .from('public_business_profile')
          .select('id,full_name,booking_headline,booking_bio,booking_avatar,timezone,booking_enabled,razorpay_key_id,biz_name,plan_id')
          .eq('booking_username', username)
          .maybeSingle();

        if (hErr || !hostData) { setError('Booking page not found.'); setLoading(false); return; }
        if (!hostData.booking_enabled) { setError(`${hostData.full_name || username}'s booking page is currently disabled.`); setLoading(false); return; }
        setHost(hostData);

        // Get their meeting types
        const { data: types } = await supabase
          .from('meeting_types')
          .select('*')
          .eq('user_id', hostData.id)
          .eq('is_active', true)
          .order('position');
        setMeetingTypes(types || []);

        // Get their availability
        const { data: avail } = await supabase
          .from('availability_slots')
          .select('*')
          .eq('user_id', hostData.id)
          .eq('is_active', true);
        setAvailability(avail || []);
      } catch(e) {
        setError('Failed to load booking page. Please try again.');
      }
      setLoading(false);
    })();
  }, [username]);

  // Next 21 available days based on host availability
  const availableDays = useMemo(() => {
    const days = [];
    const now = new Date();
    for (let i = 1; i <= 60 && days.length < 21; i++) {
      const d = new Date(now);
      d.setDate(now.getDate() + i);
      const dow = d.getDay();
      if (availability.find(a => a.day_of_week === dow && a.is_active)) {
        days.push(d.toISOString().split('T')[0]);
      }
    }
    return days;
  }, [availability]);

  // Fetch existing meetings to block booked slots
  const [existingMeetings, setExistingMeetings] = React.useState([]);
  useEffect(() => {
    if (!selDate || !host) return;
    supabase.from('meetings')
      .select('start_time,end_time,status')
      .eq('host_id', host.id)
      .neq('status', 'cancelled')
      .gte('start_time', selDate + 'T00:00:00.000Z')
      .lte('start_time', selDate + 'T23:59:59.999Z')
      .then(({ data }) => setExistingMeetings(data || []))
      .catch(() => {});
  }, [selDate, host]);

  // Generate slots, excluding already-booked times
  const slots = useMemo(() => {
    if (!selDate || !selType) return [];
    const d   = new Date(selDate + 'T12:00:00');
    const dow = d.getDay();
    const avail = availability.find(a => a.day_of_week === dow);
    if (!avail) return [];
    const result = [];
    const [sh, sm] = avail.start_time.split(':').map(Number);
    const [eh, em] = avail.end_time.split(':').map(Number);
    let cur = sh * 60 + sm;
    const end = eh * 60 + em;
    const step = selType.duration_mins + (selType.buffer_mins || 0);
    while (cur + selType.duration_mins <= end) {
      const hh = String(Math.floor(cur / 60)).padStart(2, '0');
      const mm = String(cur % 60).padStart(2, '0');
      const slotStart = new Date(`${selDate}T${hh}:${mm}:00+05:30`); // IST
      const slotEnd   = new Date(slotStart.getTime() + selType.duration_mins * 60000);
      const booked = existingMeetings.some(m => {
        const mS = new Date(m.start_time), mE = new Date(m.end_time);
        return slotStart < mE && slotEnd > mS;
      });
      // Also block past slots for today
      const isPast = selDate === fmtIST(new Date()) && slotStart < new Date();
      if (!booked && !isPast) result.push(`${hh}:${mm}`);
      cur += step;
    }
    return result;
  }, [selDate, selType, availability, existingMeetings]);

  // Load Razorpay script
  const loadRazorpay = () => new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const doInsertMeeting = async (start, end, extraFields={}) => {
    const meeting = {
      host_id:         host.id,
      meeting_type_id: selType.id,
      guest_name:      form.guest_name.trim(),
      guest_email:     form.guest_email.trim(),
      guest_answers:   answers,
      notes:           form.notes,
      start_time:      start.toISOString(),
      end_time:        end.toISOString(),
      status:          'confirmed',
      guest_timezone:  Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...extraFields,
    };
    const { data, error } = await supabase.from('meetings').insert(meeting).select();
    if (error) throw error;
    return data?.[0] || meeting;
  };

  const confirmBooking = async () => {
    if (!form.guest_name.trim() || !form.guest_email.trim()) return;
    const start = new Date(`${selDate}T${selSlot}:00`);
    const end   = new Date(start.getTime() + selType.duration_mins * 60000);

    // ── Paid booking: charge via Razorpay first ───────────────────────────
    if (selType.price > 0 && host.razorpay_key_id) {
      const loaded = await loadRazorpay();
      if (!loaded) { alert('Payment system failed to load. Please try again.'); return; }

      await new Promise((resolve, reject) => {
        const rzp = new window.Razorpay({
          key:         host.razorpay_key_id,
          amount:      selType.price * 100, // paise
          currency:    'INR',
          name:        host.biz_name || host.full_name || 'Aspreo',
          description: `${selType.title} — ${selDate} at ${selSlot}`,
          prefill:     { name: form.guest_name, email: form.guest_email },
          theme:       { color: '#2B1DC9' },
          handler: async (response) => {
            try {
              setBooking(true);
              const booked = await doInsertMeeting(start, end, {
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id:   response.razorpay_order_id,
              });
              setConfirmed({ ...booked, start_time: start.toISOString(), end_time: end.toISOString() });
              setStep(4);
              resolve();
            } catch(e) { reject(e); }
            setBooking(false);
          },
          modal: { ondismiss: () => reject(new Error('Payment cancelled')) },
        });
        rzp.open();
      }).catch(e => { if(e.message !== 'Payment cancelled') alert('Payment failed: ' + e.message); });
      return;
    }

    // ── Free booking ──────────────────────────────────────────────────────
    setBooking(true);
    try {
      const booked = await doInsertMeeting(start, end);

      // ── Send confirmation email via Supabase Edge Function ─────────────────
      // Deploy this function: supabase/functions/send-booking-email/index.ts
      // It reads meeting data and sends via Resend to both host + guest
      try {
        // Call edge function directly with fetch (more reliable from public pages)
        // Edge functions always use the direct Supabase URL (not the /api/sb proxy)
        await fetch('/api/sb/functions/v1/send-booking-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY || ''}`,
          },
          body: JSON.stringify({
            meeting_id:   booked.id,
            guest_name:   form.guest_name,
            guest_email:  form.guest_email,
            host_id:      host.id,
            host_name:    host.full_name,
            meeting_type: selType.title,
            start_time:   start.toISOString(),
            end_time:     end.toISOString(),
            duration:     selType.duration_mins,
          }),
        });
      } catch(e) {
        console.warn('[booking email] edge fn not deployed yet:', e.message);
        // Booking still succeeds — email is non-blocking
      }

      // ── Google Calendar sync via Edge Function ─────────────────────────────
      // Deploy: supabase/functions/google-calendar-sync/index.ts
      // Reads host's google_refresh_token from profiles, creates event
      try {
        await fetch('/api/sb/functions/v1/google-calendar-sync', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY || ''}` },
          body: JSON.stringify({
            host_id:     host.id,
            meeting_id:  booked.id,
            start_time:  start.toISOString(),
            end_time:    end.toISOString(),
            title:       `${selType.title} with ${form.guest_name}`,
            guest_email: form.guest_email,
          }),
        });
      } catch(e) {
        console.warn('[gcal sync] edge fn not deployed yet:', e.message);
      }

      setConfirmed({ ...booked, start_time: start.toISOString(), end_time: end.toISOString() });
      setStep(4);
    } catch(e) {
      alert('Booking failed: ' + e.message);
    }
    setBooking(false);
  };

  // ── Styles ─────────────────────────────────────────────────────────────────
  const card = { background:'#fff', border:'1px solid #E4E4EC', borderRadius:16, padding:24, maxWidth:540, width:'100%', margin:'0 auto', boxShadow:'0 8px 32px rgba(43,29,201,.10)' };
  const inp  = { width:'100%', padding:'10px 14px', border:'1.5px solid #E4E4EC', borderRadius:9, fontFamily:FONT, fontSize:14, outline:'none', boxSizing:'border-box', color:'#0F0E1A', transition:'border-color .15s' };
  const btnP = { background:PRIMARY, color:'#fff', border:'none', borderRadius:9, padding:'12px 24px', cursor:'pointer', fontFamily:FONT, fontSize:14, fontWeight:700, width:'100%', transition:'opacity .15s' };
  const btnG = { background:'#F0F0F5', color:'#4A4A6A', border:'none', borderRadius:9, padding:'10px 18px', cursor:'pointer', fontFamily:FONT, fontSize:13, fontWeight:600, transition:'background .15s' };

  const stepBar = (
    <div style={{ display:'flex', justifyContent:'center', gap:6, marginBottom:24, alignItems:'center' }}>
      {['Type','Date','Time','Details','Done'].map((s, i) => (
        <React.Fragment key={s}>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
            <div style={{ width:26, height:26, borderRadius:'50%', background: i <= step ? PRIMARY : '#E4E4EC', color: i <= step ? '#fff' : '#9CA3AF', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, transition:'background .2s' }}>{i + 1}</div>
            <div style={{ fontSize:9, color: i <= step ? PRIMARY : '#9CA3AF', fontWeight: i <= step ? 700 : 400 }}>{s}</div>
          </div>
          {i < 4 && <div style={{ width:28, height:2, background: i < step ? PRIMARY : '#E4E4EC', marginBottom:14, borderRadius:2, transition:'background .2s' }}/>}
        </React.Fragment>
      ))}
    </div>
  );

  if (loading) return (
    <div style={{ minHeight:'100vh', background:'#F6F6F8', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:FONT }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:48, height:48, border:`3px solid ${PRIMARY}`, borderTopColor:'transparent', borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto 16px' }}/>
        <div style={{ color:'#4A4A6A', fontSize:14 }}>Loading booking page...</div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight:'100vh', background:'#F6F6F8', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:FONT, padding:24 }}>
      <div style={{ ...card, textAlign:'center', padding:48 }}>
        <div style={{ fontSize:48, marginBottom:16 }}>📭</div>
        <div style={{ fontSize:18, fontWeight:700, color:'#0F0E1A', marginBottom:8 }}>Page Not Found</div>
        <div style={{ color:'#4A4A6A', fontSize:14 }}>{error}</div>
        <div style={{ marginTop:24 }}>
          <a href="https://www.aspreo.com" style={{ color:PRIMARY, fontSize:13, fontWeight:600 }}>← Back to Aspreo</a>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:'100vh', background:'#F6F6F8', fontFamily:FONT, padding:'32px 16px' }}>
      {/* Host header */}
      <div style={{ textAlign:'center', marginBottom:28 }}>
        {host.booking_avatar && (
          <img src={host.booking_avatar} alt={host.full_name} style={{ width:72, height:72, borderRadius:'50%', objectFit:'cover', border:`3px solid ${PRIMARY}`, marginBottom:12 }}/>
        )}
        {!host.booking_avatar && (
          <div style={{ width:72, height:72, borderRadius:'50%', background:PRIMARY, color:'#fff', fontSize:28, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px' }}>
            {(host.full_name||username)[0].toUpperCase()}
          </div>
        )}
        <div style={{ fontSize:22, fontWeight:800, color:'#0F0E1A', marginBottom:4 }}>{host.full_name || username}</div>
        {host.booking_headline && <div style={{ fontSize:14, color:'#4A4A6A', marginBottom:4 }}>{host.booking_headline}</div>}
        {host.booking_bio && <div style={{ fontSize:13, color:'#8888AA', maxWidth:440, margin:'0 auto' }}>{host.booking_bio}</div>}
        <div style={{ marginTop:10, fontSize:11, color:'#8888AA' }}>Powered by <strong style={{ color:PRIMARY }}>Aspreo</strong></div>
      </div>

      <div style={card}>
        {stepBar}

        {/* STEP 0 — Select Meeting Type */}
        {step === 0 && (
          <div>
            <div style={{ fontWeight:700, fontSize:16, color:'#0F0E1A', marginBottom:16, textAlign:'center' }}>What type of meeting?</div>
            {meetingTypes.length === 0 && (
              <div style={{ textAlign:'center', color:'#8888AA', padding:32 }}>No meeting types available yet.</div>
            )}
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {meetingTypes.map(mt => (
                <div key={mt.id} onClick={() => { setSelType(mt); setStep(1); }}
                  style={{ border:`2px solid ${mt.color || PRIMARY}20`, borderRadius:12, padding:'14px 18px', cursor:'pointer', transition:'all .15s', background: selType?.id === mt.id ? mt.color + '12' : '#FAFAFA' }}
                  onMouseEnter={e => e.currentTarget.style.background = (mt.color || PRIMARY) + '10'}
                  onMouseLeave={e => e.currentTarget.style.background = '#FAFAFA'}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div>
                      <div style={{ fontWeight:700, fontSize:15, color: mt.color || PRIMARY }}>{mt.title}</div>
                      {mt.description && <div style={{ fontSize:12, color:'#8888AA', marginTop:3 }}>{mt.description}</div>}
                    </div>
                    <div style={{ textAlign:'right', flexShrink:0, marginLeft:12 }}>
                      <div style={{ fontSize:14, fontWeight:700, color:'#0F0E1A' }}>{mt.duration_mins} min</div>
                      <div style={{ fontSize:12, color: mt.price > 0 ? '#16A34A' : '#8888AA', fontWeight:600 }}>
                        {mt.price > 0 ? `₹${mt.price}` : 'Free'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* STEP 1 — Select Date */}
        {step === 1 && (
          <div>
            <div style={{ fontWeight:700, fontSize:16, color:'#0F0E1A', marginBottom:4, textAlign:'center' }}>Select a Date</div>
            <div style={{ textAlign:'center', fontSize:13, color:'#8888AA', marginBottom:16 }}>{selType?.title} · {selType?.duration_mins} min</div>
            {availableDays.length === 0 && (
              <div style={{ textAlign:'center', color:'#8888AA', padding:32 }}>No available dates. Host hasn't set availability yet.</div>
            )}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:16 }}>
              {availableDays.map(d => {
                const dt  = new Date(d + 'T12:00:00');
                const sel = d === selDate;
                return (
                  <div key={d} onClick={() => { setSelDate(d); setStep(2); }}
                    style={{ background: sel ? PRIMARY : '#F6F6F8', border:`1.5px solid ${sel ? PRIMARY : '#E4E4EC'}`, borderRadius:10, padding:'10px 6px', textAlign:'center', cursor:'pointer', transition:'all .15s' }}
                    onMouseEnter={e => { if (!sel) e.currentTarget.style.borderColor = PRIMARY; }}
                    onMouseLeave={e => { if (!sel) e.currentTarget.style.borderColor = '#E4E4EC'; }}>
                    <div style={{ fontSize:10, color: sel ? 'rgba(255,255,255,.8)' : '#8888AA', fontWeight:600 }}>{DAYS_SHORT[dt.getDay()]}</div>
                    <div style={{ fontSize:18, fontWeight:800, color: sel ? '#fff' : '#0F0E1A', margin:'2px 0' }}>{dt.getDate()}</div>
                    <div style={{ fontSize:10, color: sel ? 'rgba(255,255,255,.7)' : '#8888AA' }}>{dt.toLocaleDateString('en-IN', { month:'short' })}</div>
                  </div>
                );
              })}
            </div>
            <button onClick={() => setStep(0)} style={btnG}>← Back</button>
          </div>
        )}

        {/* STEP 2 — Select Time Slot */}
        {step === 2 && (
          <div>
            <div style={{ fontWeight:700, fontSize:16, color:'#0F0E1A', marginBottom:4, textAlign:'center' }}>Select a Time</div>
            <div style={{ textAlign:'center', fontSize:13, color:'#8888AA', marginBottom:16 }}>
              {new Date(selDate + 'T12:00:00').toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long' })}
              {' · '}{Intl.DateTimeFormat().resolvedOptions().timeZone}
            </div>
            {slots.length === 0 && <div style={{ textAlign:'center', color:'#8888AA', padding:32 }}>No slots available this day.</div>}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, marginBottom:16 }}>
              {slots.map(s => {
                const sel = s === selSlot;
                return (
                  <div key={s} onClick={() => { setSelSlot(s); setStep(3); }}
                    style={{ background: sel ? PRIMARY : '#F6F6F8', border:`1.5px solid ${sel ? PRIMARY : '#E4E4EC'}`, borderRadius:9, padding:'10px', textAlign:'center', cursor:'pointer', fontWeight:700, fontSize:14, color: sel ? '#fff' : '#0F0E1A', transition:'all .15s' }}
                    onMouseEnter={e => { if (!sel) { e.currentTarget.style.background = PRIMARY + '10'; e.currentTarget.style.borderColor = PRIMARY; } }}
                    onMouseLeave={e => { if (!sel) { e.currentTarget.style.background = '#F6F6F8'; e.currentTarget.style.borderColor = '#E4E4EC'; } }}>
                    {s}
                  </div>
                );
              })}
            </div>
            <button onClick={() => setStep(1)} style={btnG}>← Back</button>
          </div>
        )}

        {/* STEP 3 — Guest Details */}
        {step === 3 && (
          <div>
            <div style={{ fontWeight:700, fontSize:16, color:'#0F0E1A', marginBottom:4, textAlign:'center' }}>Your Details</div>
            <div style={{ textAlign:'center', fontSize:13, color:'#8888AA', marginBottom:20 }}>
              {selType?.title} · {new Date(selDate + 'T12:00:00').toLocaleDateString('en-IN', { weekday:'short', day:'numeric', month:'short' })} at {selSlot}
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:14 }}>
              <input value={form.guest_name}  onChange={e => upd('guest_name', e.target.value)}  placeholder="Your Full Name *" style={inp}
                onFocus={e => e.target.style.borderColor = PRIMARY} onBlur={e => e.target.style.borderColor = '#E4E4EC'}/>
              <input value={form.guest_email} onChange={e => upd('guest_email', e.target.value)} placeholder="Your Email *" type="email" style={inp}
                onFocus={e => e.target.style.borderColor = PRIMARY} onBlur={e => e.target.style.borderColor = '#E4E4EC'}/>
              {(selType?.questions || []).map(q => (
                <div key={q.id}>
                  <div style={{ fontSize:12, color:'#4A4A6A', marginBottom:5, fontWeight:600 }}>{q.label}{q.required && <span style={{ color:'#DC2626' }}> *</span>}</div>
                  <input value={answers[q.id] || ''} onChange={e => setAnswers(p => ({ ...p, [q.id]: e.target.value }))} style={inp}
                    onFocus={e => e.target.style.borderColor = PRIMARY} onBlur={e => e.target.style.borderColor = '#E4E4EC'}/>
                </div>
              ))}
              <textarea value={form.notes} onChange={e => upd('notes', e.target.value)} placeholder="Anything you'd like to share before the meeting? (optional)"
                style={{ ...inp, height:80, resize:'vertical', lineHeight:1.6 }}
                onFocus={e => e.target.style.borderColor = PRIMARY} onBlur={e => e.target.style.borderColor = '#E4E4EC'}/>
            </div>
            {selType?.price > 0 && (
              <div style={{ background:'#F0FDF4', border:'1.5px solid #16A34A', borderRadius:10, padding:'10px 14px', marginBottom:12, fontSize:13, color:'#15803D', fontWeight:600, textAlign:'center' }}>
                💳 This meeting requires payment of ₹{selType.price.toLocaleString('en-IN')}
                {!host?.razorpay_key_id && <div style={{fontSize:11,color:'#DC2626',marginTop:4}}>⚠️ Host has not set up Razorpay yet — contact them directly</div>}
              </div>
            )}
            <button onClick={confirmBooking}
              disabled={!form.guest_name.trim() || !form.guest_email.trim() || booking}
              style={{ ...btnP, opacity: (!form.guest_name.trim() || !form.guest_email.trim() || booking) ? 0.6 : 1, marginBottom:10, background: selType?.price > 0 && host?.razorpay_key_id ? '#16A34A' : PRIMARY }}>
              {booking ? '⏳ Processing...' : selType?.price > 0 && host?.razorpay_key_id ? `💳 Pay ₹${selType.price.toLocaleString('en-IN')} & Confirm` : '✅ Confirm Booking'}
            </button>
            <button onClick={() => setStep(2)} style={btnG}>← Back</button>
          </div>
        )}

        {/* STEP 4 — Confirmed */}
        {step === 4 && (
          <div style={{ textAlign:'center', padding:'12px 0 8px' }}>
            <div style={{ width:64, height:64, borderRadius:'50%', background:'#F0FDF4', border:'2px solid #16A34A', display:'flex', alignItems:'center', justifyContent:'center', fontSize:32, margin:'0 auto 16px' }}>✅</div>
            <div style={{ fontSize:22, fontWeight:800, color:'#0F0E1A', marginBottom:8 }}>Booking Confirmed!</div>
            <div style={{ fontSize:14, color:'#4A4A6A', marginBottom:20 }}>
              A confirmation email has been sent to <strong>{form.guest_email}</strong>
            </div>
            <div style={{ background:'#F0FDFA', border:'1px solid #0D9488', borderRadius:12, padding:'16px 20px', marginBottom:20, textAlign:'left' }}>
              <div style={{ fontWeight:700, color:TEAL, marginBottom:8, fontSize:15 }}>{selType?.title}</div>
              <div style={{ fontSize:13, color:'#4A4A6A', lineHeight:2 }}>
                <div>📅 {new Date(confirmed?.start_time).toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}</div>
                <div>🕐 {new Date(confirmed?.start_time).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' })} — {new Date(confirmed?.end_time).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' })}</div>
                <div>👤 {form.guest_name}</div>
                <div>📧 {form.guest_email}</div>
              </div>
            </div>
            {/* ── Add to Calendar buttons ── */}
            {confirmed && (() => {
              const start = new Date(confirmed.start_time);
              const end   = new Date(confirmed.end_time);
              const fmt   = (d) => d.toISOString().replace(/[-:]/g,'').replace(/\.\d{3}/,'');
              const title = encodeURIComponent(`${selType?.title} with ${host?.full_name || 'Aspreo'}`);
              const details = encodeURIComponent(`Booked via Aspreo. Guest: ${form.guest_name} (${form.guest_email})`);
              const gcalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${fmt(start)}/${fmt(end)}&details=${details}`;
              const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${title}&startdt=${start.toISOString()}&enddt=${end.toISOString()}&body=${details}`;
              return (
                <div style={{ marginBottom:20 }}>
                  <div style={{ fontSize:12, color:'#4A4A6A', fontWeight:600, marginBottom:10 }}>📅 Add to your calendar:</div>
                  <div style={{ display:'flex', gap:8, justifyContent:'center', flexWrap:'wrap' }}>
                    <a href={gcalUrl} target="_blank" rel="noreferrer"
                      style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'9px 16px', borderRadius:9, border:'1.5px solid #4285F4', background:'#fff', color:'#4285F4', textDecoration:'none', fontSize:13, fontWeight:700 }}>
                      <img src="https://www.google.com/favicon.ico" alt="" style={{ width:14, height:14 }}/> Google Calendar
                    </a>
                    <a href={outlookUrl} target="_blank" rel="noreferrer"
                      style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'9px 16px', borderRadius:9, border:'1.5px solid #0078D4', background:'#fff', color:'#0078D4', textDecoration:'none', fontSize:13, fontWeight:700 }}>
                      <img src="https://outlook.live.com/favicon.ico" alt="" style={{ width:14, height:14 }}/> Outlook
                    </a>
                  </div>
                </div>
              );
            })()}
            <div style={{ fontSize:12, color:'#8888AA', marginBottom:20 }}>
              If you need to reschedule, reply to the confirmation email.
            </div>
            <button onClick={() => { setStep(0); setSelType(null); setSelDate(''); setSelSlot(''); setForm({ guest_name:'', guest_email:'', notes:'' }); setAnswers({}); setConfirmed(null); }}
              style={btnP}>Book Another Meeting</button>
            {(host?.plan_id==="hobby"||!host?.plan_id)&&(
              <div style={{ marginTop:16, fontSize:12, color:'#8888AA' }}>
                Powered by <a href="https://www.aspreo.com" style={{ color:PRIMARY, fontWeight:700, textDecoration:'none' }}>Aspreo</a>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
